import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useDropzone } from 'react-dropzone';
import makeStyles from '@mui/styles/makeStyles';
import {
  Avatar,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  Input,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@mui/material';
import UploadIcon from '@mui/icons-material/CloudUpload';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useFileTypesStore } from '@lib/store';
import { TextUtils } from '@shared/utils';
import { ApiUtils, ApplicationApi, type CountryEntity, FileApi, type FileEntity } from '@shared/api';
import { SnackbarContext, PDFView } from '@widgets';
import { CustomDatePicker } from '@pages/Applications/widgets/CustomDatePicker';
import { Button, DelayedLinearProgress } from '@shared/widgets';

type FileItem = File & {
  preview: string;
  fileEntity?: FileEntity;
  fileType: string;
  country?: string;
  number?: string;
  expiryDate?: string;
  citizenByInvestment?: boolean;
};

type Params = {
  selectedApplicationId: string;
  manualApplicationId: string;
};

const useStyles = makeStyles<Theme>((theme) => ({
  postTransactionContainer: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.primary.main,
  },
  card: {
    minWidth: '340px',
  },
  cardDesign: {
    marginBottom: 20,
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.primary.contrastText,
  },
  appBar: {
    marginBottom: theme.spacing(2),
  },
  title: {
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'block',
    },
  },
  grow: {
    flexGrow: 1,
  },
  dropArea: {
    textAlign: 'center',
    padding: theme.spacing(7),
  },
  AreaArea: {
    textAlign: 'center',
    paddingBottom: theme.spacing(2),
  },
  button: {
    marginRight: theme.spacing(1),
  },
  formControl: {
    textAlign: 'center',
    minWidth: 200,
    padding: theme.spacing(2),
  },
  fileTypeDropdown: {
    marginBottom: theme.spacing(2),
  },
}));

const TYPE_PASSPORT = '646950001';
const TYPE_PASSPORT_CRM = 'Copy of Passport/ID';

interface FileTypesIndex {
  [key: string]: string;
}

interface FileUploadProps {
  circles?: string[];
  countries?: CountryEntity[];
  uploadedFiles?: FileEntity[];
  nationalitiesCount?: number;
  onUpload?: (id?: string) => void;
}

export const FileUpload = (props: FileUploadProps) => {
  const { selectedApplicationId, manualApplicationId } = useParams<Params>();

  const { loading: loadingFileTypes, data: fileTypes, getFileTypes } = useFileTypesStore();

  const [uploading, setUploading] = useState<boolean>(false);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [fileTypesIndex, setFileTypesIndex] = useState<FileTypesIndex>({});
  //const [mimeTypes, setMimeTypes] = useState<string[]>([]);
  const [selectedFileType, setSelectedFileType] = useState<string>('');
  const [filePreview, setFilePreview] = useState<string>('');
  const [ippGroup, setIppGroup] = useState<string | undefined>();
  const [passportNumber, setPassportNumber] = useState<string | undefined>();
  const [countryOfIssuance, setCountryOfIssuance] = useState<string | undefined>();
  const [expiryDate, setExpiryDate] = useState<string | undefined>();
  const [byInvestment, setByInvestment] = useState<boolean | undefined>();
  const [closedPdf, setClosedPdf] = useState<boolean>(true);
  const [objectUrl] = useState<string>();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedFileIndex, setSelectedFileIndex] = useState<number | null>(null);

  const snackbar = useContext(SnackbarContext);
  const classes = useStyles();

  useEffect(() => {
    getFileTypes();
  }, []);

  useEffect(() => {
    if (loadingFileTypes || fileTypes.length === 0) return;
    const index = fileTypes.reduce((acc, item) => {
      acc[item.id.toString()] = item.name;
      return acc;
    }, {} as FileTypesIndex);
    setFileTypesIndex(index);
  }, [fileTypes, loadingFileTypes]);

  useEffect(() => {
    return () => {
      files.forEach((file) => URL.revokeObjectURL(file.preview));
    };
  }, [files]);

  useEffect(() => {
    setPassportNumber(undefined);
    setExpiryDate(undefined);
    setCountryOfIssuance(undefined);
    setByInvestment(undefined);
  }, [selectedFileType]);

  const openMenuHandler = (e: React.MouseEvent<HTMLButtonElement>, idx: number) => {
    setMenuAnchor(e.currentTarget);
    setSelectedFileIndex(idx);
  };

  const closeMenuHandler = () => {
    setMenuAnchor(null);
    setSelectedFileIndex(null);
  };

  const submitHandler = async (files: FileItem[]) => {
    try {
      setUploading(true);
      if (selectedApplicationId) {
        await FileApi.uploadFiles(files, selectedApplicationId);
        snackbar.success('File Upload Successful');
        setFiles([]);
        props.onUpload?.();
      } else {
        if (props.circles && !ippGroup) {
          snackbar.error('File Upload Unsuccessful. Make sure circle is selected for upload.');
          setUploading(false);
          return;
        }
        const res = await FileApi.uploadFile(files, manualApplicationId);
        snackbar.success('File Upload Successful');
        setFiles([]);
        const id = res?.[0]?.applicationId;
        if (id && ippGroup) await ApplicationApi.assignManualApplication({ manualApplicationId: id, ippGroup });
        props.onUpload?.(id);
      }
    } catch (error) {
      snackbar.error(ApiUtils.getError(error));
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    accept: 'application/pdf',
    onDrop: (acceptedFiles: File[]) => {
      if (!selectedFileType) {
        snackbar.error('Please select a file type from the dropdown');
        return;
      }
      if (acceptedFiles.length > 1) {
        snackbar.error('Only one file of each type may be selected for upload');
        return;
      }
      if (files.some((file) => file.name === acceptedFiles[0].name)) {
        snackbar.error('Duplicate file name detected');
        return;
      }

      setSelectedFileType('');
      setFiles((prev) =>
        prev.concat(
          acceptedFiles.map((file) =>
            Object.assign(file, {
              preview: URL.createObjectURL(file),
              fileType: selectedFileType,
              country: countryOfIssuance,
              number: passportNumber,
              expiryDate: expiryDate,
              citizenshipByInvestment: byInvestment,
            }),
          ),
        ),
      );
      setPassportNumber(undefined);
      setCountryOfIssuance(undefined);
      setExpiryDate(undefined);
      setByInvestment(undefined);
    },
  });

  const removeFileHandler = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    closeMenuHandler();
  };

  const previewFileHandler = (index: number) => {
    const file = files[index];
    if (!file) return;
    setFilePreview(URL.createObjectURL(file));
    setClosedPdf(false);
    closeMenuHandler();
  };

  const passportIsReady =
    (selectedFileType === TYPE_PASSPORT && !!passportNumber && !!countryOfIssuance && !!expiryDate) || selectedFileType !== TYPE_PASSPORT;
  const showUploadInput = (props.circles ? !!ippGroup : true) && !!selectedFileType && passportIsReady;
  const showUploadButton = files.length > 0;

  const uploadedPassports = props.uploadedFiles?.reduce((result, item) => result + (item.type === TYPE_PASSPORT_CRM ? 1 : 0), 0);
  const pendingPassports = files?.reduce((result, item) => result + (item.fileType === TYPE_PASSPORT ? 1 : 0), 0);

  const listContainsPassports = pendingPassports > 0;
  const needPassport =
    props.nationalitiesCount !== undefined &&
    uploadedPassports !== undefined &&
    pendingPassports !== undefined &&
    props.nationalitiesCount - uploadedPassports - pendingPassports > 0;

  const filteredFileTypes = fileTypes.filter((fileType) =>
    fileType.id.toString() === TYPE_PASSPORT ? needPassport : files.findIndex((f) => f.fileType === fileType.id.toString()) === -1,
  );

  return (
    <>
      <DelayedLinearProgress loading={loadingFileTypes} />
      <Card className={classes.card}>
        <CardHeader title='Upload Files' />
        <CardContent>
          {props.circles && (
            <FormControl fullWidth margin='normal' required>
              <InputLabel>Circle</InputLabel>
              <Select
                variant='standard'
                name='ippGroup'
                label='Circle'
                fullWidth
                required
                value={ippGroup || ''}
                onChange={(e) => setIppGroup(e.target.value)}
              >
                {props.circles.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl fullWidth className={classes.fileTypeDropdown}>
            <InputLabel id='fileTypeLabel'>Select a file type</InputLabel>
            <Select
              variant='standard'
              labelId='fileTypeLabel'
              fullWidth
              placeholder='Select a file type'
              value={selectedFileType || ''}
              onChange={({ target }) => setSelectedFileType(target.value as string)}
            >
              {filteredFileTypes.map((fileType) => (
                <MenuItem key={fileType.id} value={fileType.id.toString()}>
                  {TextUtils.pascalToWords(fileType.name)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedFileType === TYPE_PASSPORT && (
            <>
              <FormControl fullWidth className={classes.fileTypeDropdown}>
                <InputLabel id='passportNumber' required>
                  Passport/ID Number
                </InputLabel>
                <Input fullWidth value={passportNumber || ''} onChange={(e) => setPassportNumber(e.currentTarget.value)} />
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id='countryOfIssuance' required>
                  Country of Issuance
                </InputLabel>
                <Select
                  variant='standard'
                  name='countryOfIssuance'
                  label='Country of Issuance'
                  fullWidth
                  required
                  value={countryOfIssuance ?? ''}
                  onChange={(e) => setCountryOfIssuance(e.target.value as string)}
                >
                  {props.countries!.map((item) => (
                    <MenuItem key={item.name} value={item.name}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <CustomDatePicker
                  label='Expiry Date'
                  inputFormat='dd/MM/yyyy'
                  fullWidth
                  required
                  validate
                  value={expiryDate ? new Date(expiryDate) : null}
                  onChange={(value) => setExpiryDate(value?.toDateString())}
                />
              </FormControl>
              <FormControl fullWidth>
                <FormControlLabel
                  label='Citizenship by Investment'
                  control={<Checkbox checked={byInvestment || false} onChange={() => setByInvestment((current) => !current)} />}
                />
              </FormControl>
            </>
          )}
          {showUploadInput ? (
            <div className={classes.dropArea} {...getRootProps()}>
              <input {...getInputProps()} />
              <Avatar style={{ margin: '10px auto' }}>
                <UploadIcon />
              </Avatar>
              {isDragActive && !isDragReject && <Typography variant='body1'>Drop files here</Typography>}
              {!isDragActive && <Typography variant='body1'>Click here or drop files to upload.</Typography>}
              {isDragReject && <Typography variant='body1'>File type not accepted.</Typography>}
            </div>
          ) : null}

          <div className={classes.AreaArea}>
            {files.length > 0 && !isDragActive && (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Name</TableCell>
                    {listContainsPassports && (
                      <>
                        <TableCell>ID/Number</TableCell>
                        <TableCell>Country</TableCell>
                        <TableCell>By Investment</TableCell>
                      </>
                    )}
                    <TableCell>Size</TableCell>
                    <TableCell align='right'>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {files.map((file, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{TextUtils.pascalToWords(fileTypesIndex[file.fileType])}</TableCell>
                      <TableCell
                        style={{
                          maxWidth: '120px',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {file.name}
                      </TableCell>
                      {listContainsPassports && (
                        <>
                          <TableCell>{file.number}</TableCell>
                          <TableCell>{file.country}</TableCell>
                          <TableCell>{file.fileType === TYPE_PASSPORT ? (file.citizenByInvestment ? 'Yes' : 'No') : null}</TableCell>
                        </>
                      )}
                      <TableCell>{(file.size / 1000).toFixed(1)} kB</TableCell>
                      <TableCell align='right'>
                        <IconButton onClick={(e) => openMenuHandler(e, idx)} size='large'>
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor) && selectedFileIndex !== null} onClose={closeMenuHandler}>
              <MenuItem onClick={() => selectedFileIndex !== null && previewFileHandler(selectedFileIndex)}>Preview</MenuItem>
              <MenuItem onClick={() => selectedFileIndex !== null && removeFileHandler(selectedFileIndex)}>Remove</MenuItem>
            </Menu>
          </div>

          {showUploadButton && (
            <Button variant='contained' loading={uploading} color='primary' onClick={() => submitHandler(files)} className={classes.button}>
              Upload
            </Button>
          )}
        </CardContent>
      </Card>
      {!closedPdf && filePreview && <PDFView objectUrl={filePreview} onClose={() => setClosedPdf(true)} />}
      {!closedPdf && objectUrl && <PDFView objectUrl={objectUrl} onClose={() => setClosedPdf(true)} />}
    </>
  );
};
